import React from 'react';
import {
  Box, Container, Typography, Grid, Chip,
} from '@mui/material';
import {
  AppRegistration, GroupAdd, Payments, ArrowForward,
} from '@mui/icons-material';

const steps = [
  {
    number: '01',
    icon: <AppRegistration sx={{ fontSize: 28 }} />,
    title: 'Crie a conta da empresa',
    description:
      'Registe-se em menos de 2 minutos. Escolha a Plataforma de estafetas para equipas de entregas ou a Plataforma de motoristas para frotas. Comece já com um plano gratuito — sem cartão de crédito.',
    color: '#1e40af',
    bg: 'rgba(30,64,175,0.1)',
    badge: 'Começar grátis',
  },
  {
    number: '02',
    icon: <GroupAdd sx={{ fontSize: 28 }} />,
    title: 'Adicione a equipa',
    description:
      'Importe estafetas ou motoristas por CSV ou adicione manualmente. Atribua parceiros, configure veículos e defina comissões e taxas em minutos.',
    color: '#16a34a',
    bg: 'rgba(22,163,74,0.1)',
    badge: 'Importação CSV em massa',
  },
  {
    number: '03',
    icon: <Payments sx={{ fontSize: 28 }} />,
    title: 'Gere relatórios e pague',
    description:
      'No fim de cada semana, gere relatórios de pagamento para toda a equipa. Exporte um ficheiro XML SEPA para transferências em lote e envie faturas em PDF, por correio eletrónico ou WhatsApp.',
    color: '#ea580c',
    bg: 'rgba(234,88,12,0.1)',
    badge: 'SEPA + faturas',
  },
];

export default function HowItWorks() {
  return (
    <Box
      id="how-it-works"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
      }}
    >
      <Container maxWidth="lg">
        {/* Section header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="COMO FUNCIONA"
            sx={{
              mb: 2,
              background: 'rgba(22,163,74,0.1)',
              color: '#16a34a',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.1em',
              height: 28,
            }}
          />
          <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Operacional em{' '}
            <Box component="span" sx={{
              background: 'linear-gradient(135deg, #16a34a, #1e40af)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              menos de 10 minutos
            </Box>
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '1.05rem', maxWidth: 520, mx: 'auto' }}>
            Sem formação complexa. Sem configurações confusas. Apenas ferramentas diretas
            que funcionam como o seu negócio.
          </Typography>
        </Box>

        {/* Steps */}
        <Box sx={{ position: 'relative' }}>
          {/* Connecting line (desktop) */}
          <Box sx={{
            position: 'absolute',
            top: 52,
            left: 'calc(16.67% + 48px)',
            right: 'calc(16.67% + 48px)',
            height: 2,
            background: 'linear-gradient(90deg, #1e40af, #16a34a, #ea580c)',
            display: { xs: 'none', md: 'block' },
            zIndex: 0,
            opacity: 0.3,
          }} />

          <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
            {steps.map((step, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                  {/* Step icon circle */}
                  <Box sx={{ position: 'relative', mb: 3 }}>
                    <Box sx={{
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      background: step.bg,
                      border: `2px solid ${step.color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: step.color,
                      position: 'relative',
                      zIndex: 1,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                        boxShadow: `0 0 0 8px ${step.color}15`,
                      },
                    }}>
                      {step.icon}
                    </Box>
                    {/* Step number badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: step.color,
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 10px ${step.color}40`,
                    }}>
                      {i + 1}
                    </Box>
                  </Box>

                  {/* Badge */}
                  <Chip
                    label={step.badge}
                    size="small"
                    sx={{
                      mb: 1.5,
                      background: step.bg,
                      color: step.color,
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      height: 24,
                    }}
                  />

                  <Typography variant="h5" sx={{ mb: 1.5, fontSize: '1.1rem', fontWeight: 700 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.7, maxWidth: 320 }}>
                    {step.description}
                  </Typography>

                  {/* Arrow between steps */}
                  {i < steps.length - 1 && (
                    <ArrowForward
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        mt: 2,
                        color: '#cbd5e1',
                        transform: 'rotate(90deg)',
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom illustration — two panels side by side */}
        <Grid container spacing={3} sx={{ mt: 8 }}>
          {[
            {
              title: 'Plataforma de estafetas',
              color: '#16a34a',
              bg: 'linear-gradient(135deg, rgba(22,163,74,0.08), rgba(22,163,74,0.03))',
              border: 'rgba(22,163,74,0.2)',
              icon: '📦',
              items: ['Equipas de estafetas', 'Comissões de parceiros', 'Relatórios semanais de remuneração', 'Exportações SEPA'],
            },
            {
              title: 'Plataforma de motoristas',
              color: '#ea580c',
              bg: 'linear-gradient(135deg, rgba(234,88,12,0.08), rgba(234,88,12,0.03))',
              border: 'rgba(234,88,12,0.2)',
              icon: '🚗',
              items: ['Frotas de motoristas', 'Rendas de veículos', 'Pagamentos a proprietários', 'Exportações SEPA'],
            },
          ].map((panel) => (
            <Grid item xs={12} md={6} key={panel.title}>
              <Box sx={{
                background: panel.bg,
                border: `1px solid ${panel.border}`,
                borderRadius: 3,
                p: 3.5,
              }}>
                <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>{panel.icon}</Typography>
                <Typography variant="h6" sx={{ color: panel.color, fontWeight: 700, mb: 2 }}>
                  {panel.title}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {panel.items.map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: panel.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Box sx={{ width: 6, height: 4, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px, -1px)' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 500 }}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
